import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { partnerApi } from '../../services/api/partnerApi'
import type {
  PartnerDeviceWithAssignment,
  PartnerDeviceAssignmentFilter,
} from '../../types/partner'

export default function MyDevices() {
  const navigate = useNavigate()
  const [devices, setDevices] = useState<PartnerDeviceWithAssignment[]>([])
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<PartnerDeviceAssignmentFilter>('all')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await partnerApi.listDevicesWithAssignment({
        search: search.trim() || undefined,
        assignment: filter,
      })
      setDevices(res.devices)
      setTotal(res.total)
    } catch (e: any) {
      setError(e?.message || 'Failed to load devices')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    load()
  }

  const counts = useMemo(() => {
    const assigned = devices.filter((d) => d.assignment).length
    return { total: devices.length, assigned, unassigned: devices.length - assigned }
  }, [devices])

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Devices</h1>
        <p className="text-sm text-gray-500">
          All RICs in your inventory — sold and unsold. Showing {counts.total} of {total} (
          {counts.assigned} assigned, {counts.unassigned} unassigned). New devices are
          allocated to your inventory by an admin.
        </p>
      </div>

      <form
        onSubmit={handleSearchSubmit}
        className="flex flex-col sm:flex-row gap-2 items-stretch"
      >
        <div className="relative flex-1">
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Sigfox ID or serial"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as PartnerDeviceAssignmentFilter)}
          className="px-3 py-2 border border-gray-300 rounded-lg"
        >
          <option value="all">All</option>
          <option value="unassigned">Unassigned</option>
          <option value="assigned">Assigned</option>
        </select>
        <button
          type="submit"
          className="px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
        >
          Search
        </button>
      </form>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Sigfox ID</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Serial</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">HW</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Registered</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Assigned to</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {error && !loading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-red-600">
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && devices.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                  No devices match. New devices appear here once an admin allocates them to you.
                </td>
              </tr>
            )}
            {!loading &&
              !error &&
              devices.map((d) => (
                <tr key={d.deviceId} className="border-t border-gray-100">
                  <td className="px-4 py-3 font-mono">{d.wisolId}</td>
                  <td className="px-4 py-3">{d.serialNumber || '—'}</td>
                  <td className="px-4 py-3">{d.hwVersion}</td>
                  <td className="px-4 py-3">{d.sigfoxRegistered ? 'Y' : 'N'}</td>
                  <td className="px-4 py-3">
                    {d.assignment?.kind === 'active' ? (
                      <Link
                        to={`/partner/customers/${d.assignment.userId}`}
                        className="text-blue-600 hover:underline"
                      >
                        {d.assignment.userEmail}
                        <span className="text-gray-500"> · {d.assignment.groupName}</span>
                      </Link>
                    ) : d.assignment?.kind === 'pending' ? (
                      <Link
                        to={`/partner/customers/pending/${d.assignment.pendingAccountId}`}
                        className="text-yellow-700 hover:underline"
                      >
                        {d.assignment.email}
                        <span className="text-gray-500"> · pending invite</span>
                      </Link>
                    ) : (
                      <span className="text-gray-500">Unassigned</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {d.assignment?.kind === 'active' ? (
                      <Link
                        to={`/partner/customers/${d.assignment.userId}`}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        View
                      </Link>
                    ) : d.assignment?.kind === 'pending' ? (
                      <Link
                        to={`/partner/customers/pending/${d.assignment.pendingAccountId}`}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        View
                      </Link>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/partner/customers/invite?deviceId=${d.deviceId}`)
                        }
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Assign
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

    </div>
  )
}
